import lottie from 'lottie-web';

export default class LottieLoader {
    constructor(container, options = {}) {
        this.container = container;
        this.options = {
            animationData: null,
            path: null,
            loop: true,
            autoplay: true,
            ...options
        };
        this.animation = null;
        this.isVisible = false;
    }

    // Configuración por defecto para animación de carga
    static getDefaultUploadAnimation() {
        return {
            // Usar la animación existente del proyecto
            path: '/static/loader.json',
            loop: true,
            autoplay: true,
            rendererSettings: {
                preserveAspectRatio: 'xMidYMid slice'
            }
        };
    }

    // Configuración para animación de éxito (usar la misma animación pero más rápida)
    static getSuccessAnimation() {
        return {
            path: '/static/loader.json',
            loop: false,
            autoplay: true,
            rendererSettings: {
                preserveAspectRatio: 'xMidYMid slice'
            }
        };
    }

    // Configuración para animación de error (usar la misma animación)
    static getErrorAnimation() {
        return {
            path: '/static/loader.json',
            loop: false,
            autoplay: true,
            rendererSettings: {
                preserveAspectRatio: 'xMidYMid slice'
            }
        };
    }

    init() {
        if (!this.container) {
            console.error('LottieLoader: Container is required');
            return;
        }

        try {
            this.animation = lottie.loadAnimation({
                container: this.container,
                ...this.options
            });

            this.animation.addEventListener('DOMLoaded', () => {
                console.log('Lottie animation loaded successfully');
            });

            this.animation.addEventListener('error', (error) => {
                console.error('Lottie animation error:', error);
                this.showFallbackLoader();
            });

        } catch (error) {
            console.error('Error initializing Lottie animation:', error);
            this.showFallbackLoader();
        }
    }

    show() {
        if (this.container) {
            this.container.style.display = 'flex';
            this.container.classList.remove('hidden');
            this.container.classList.add('lottie-fade-in');
            this.isVisible = true;
            console.log('LottieLoader: Animation shown');
        }
        if (this.animation) {
            this.animation.play();
        }
    }

    hide() {
        if (this.container) {
            this.container.classList.add('lottie-fade-out');
            setTimeout(() => {
                this.container.style.display = 'none';
                this.container.classList.remove('lottie-fade-in', 'lottie-fade-out');
                this.isVisible = false;
                console.log('LottieLoader: Animation hidden');
            }, 300);
        }
        if (this.animation) {
            this.animation.pause();
        }
    }

    play() {
        if (this.animation) {
            this.animation.play();
        }
    }

    pause() {
        if (this.animation) {
            this.animation.pause();
        }
    }

    stop() {
        if (this.animation) {
            this.animation.stop();
        }
    }

    setSpeed(speed) {
        if (this.animation) {
            this.animation.setSpeed(speed);
        }
    }

    // Fallback loader en caso de que la animación Lottie no cargue
    showFallbackLoader() {
        if (this.container) {
            this.container.innerHTML = `
                <div class="flex flex-col items-center justify-center p-8">
                    <div class="relative">
                        <div class="w-16 h-16 border-4 border-petrol-200 border-t-petrol rounded-full animate-spin"></div>
                        <div class="absolute inset-0 flex items-center justify-center">
                            <svg class="w-8 h-8 text-petrol" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd"></path>
                            </svg>
                        </div>
                    </div>
                    <p class="mt-4 text-sm text-gray-600">Subiendo fotos...</p>
                </div>
            `;
        }
    }

    // Método para cambiar la animación dinámicamente
    changeAnimation(newOptions) {
        if (this.animation) {
            this.animation.destroy();
        }
        
        this.options = { ...this.options, ...newOptions };
        this.init();
    }

    // Método para mostrar animación de progreso con porcentaje
    showProgressAnimation(progress = 0) {
        if (this.container) {
            this.container.innerHTML = `
                <div class="flex flex-col items-center justify-center p-8">
                    <div class="relative w-24 h-24">
                        <svg class="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
                            <circle
                                cx="50"
                                cy="50"
                                r="40"
                                stroke="currentColor"
                                stroke-width="8"
                                fill="transparent"
                                class="text-petrol-200"
                            />
                            <circle
                                cx="50"
                                cy="50"
                                r="40"
                                stroke="currentColor"
                                stroke-width="8"
                                fill="transparent"
                                stroke-dasharray="${2 * Math.PI * 40}"
                                stroke-dashoffset="${2 * Math.PI * 40 * (1 - progress / 100)}"
                                class="text-petrol transition-all duration-300 ease-out"
                            />
                        </svg>
                        <div class="absolute inset-0 flex items-center justify-center">
                            <span class="text-lg font-semibold text-petrol">${Math.round(progress)}%</span>
                        </div>
                    </div>
                    <p class="mt-4 text-sm text-gray-600">Subiendo fotos...</p>
                </div>
            `;
        }
    }

    destroy() {
        if (this.animation) {
            this.animation.destroy();
        }
        if (this.container) {
            this.container.innerHTML = '';
        }
    }

    isVisible() {
        return this.isVisible;
    }

    getContainer() {
        return this.container;
    }

    // Método para forzar la visibilidad (útil para debugging)
    forceShow() {
        if (this.container) {
            this.container.style.display = 'flex';
            this.container.classList.remove('hidden');
            this.isVisible = true;
            console.log('LottieLoader: Animation forced to show');
        }
        if (this.animation) {
            this.animation.play();
        }
    }

    // Método para forzar el ocultamiento (útil para debugging)
    forceHide() {
        if (this.container) {
            this.container.style.display = 'none';
            this.container.classList.add('hidden');
            this.isVisible = false;
            console.log('LottieLoader: Animation forced to hide');
        }
        if (this.animation) {
            this.animation.pause();
        }
    }
} 